import { z } from 'zod'

import {
  actorKindSchema,
  auditCategorySchema,
  auditKindSchema,
  idSchema,
  isoDateSchema,
  isoInstantSchema,
  moneyPaisa,
  roleSchema,
  searchQuerySchema,
  trashEntitySchema,
} from '../common'
import { cursorQuerySchema } from '../pagination'

/**
 * O4 · `GET /audit-log` (Owner Console design 04).
 *
 * "Asif edited an animal" is useless on its own. The design shows **Was → Now**,
 * so the entry carries both sides, read out of `before_json`/`after_json`. The
 * money pair is optional like every money key: an owner sees it, and the log is
 * an owner-only endpoint anyway.
 *
 * The log is **append-only** — `REVOKE UPDATE, DELETE` plus a trigger, applying
 * to the platform owner too. There is no mutating verb on this route, and there
 * must never be one.
 */
export const auditEntrySchema = z.object({
  id: idSchema,
  at: isoInstantSchema,
  actorKind: actorKindSchema,
  actorName: z.string(),
  actorRole: roleSchema.nullish(),
  /** Set when a platform actor made the change from inside the book (P5). */
  platformAccessId: idSchema.nullish(),
  entity: trashEntitySchema.or(z.string()),
  entityId: idSchema.nullish(),
  entityLabel: z.string().nullish(),
  action: z.enum(['CREATE', 'UPDATE', 'DELETE', 'RESTORE', 'LOGIN', 'EXPORT', 'SETTING']),
  kind: auditKindSchema,
  /** The field that changed, when the entry is about one field. */
  field: z.string().nullish(),
  wasPaisa: moneyPaisa(),
  nowPaisa: moneyPaisa(),
  wasText: z.string().nullish(),
  nowText: z.string().nullish(),
  /** True when the row it describes is still in the 90-day trash (O5). */
  restorable: z.boolean(),
})

export const auditLogQuery = cursorQuerySchema.extend({
  q: searchQuerySchema.optional(),
  category: auditCategorySchema.optional(),
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
  userId: idSchema.optional(),
})

export const auditLogResponse = z.array(auditEntrySchema)

export type AuditEntry = z.infer<typeof auditEntrySchema>
export type AuditLogQuery = z.input<typeof auditLogQuery>
